import type { i18n as I18n, TFunction } from 'i18next';
import type { FieldChange } from '@api/models/common/FieldChange';
import { formatLocalized } from '../../../utils/dateFormat';
import { reportMissingChangeTranslation } from './reportMissingChangeTranslation';

const DATE_TIME_FIELDS = new Set(['deletionRequestedAt']);

/** Empty strings are cleared values, exactly like null. */
export const isEmptyChangeValue = (value: string | null | undefined): value is null | undefined => value == null || value === '';

/**
 * Display text of one value of a field change, or null when the value is empty (the caller decides how to show that,
 * because an unknown previous value and a cleared new value read differently).
 * Enum values (a collection arrives comma-separated) are translated as `enums.<enumType>.<value>`; unknown ones fall back to the raw value.
 */
export const formatChangeValue = (change: FieldChange, value: string | null | undefined, t: TFunction, i18n: I18n): string | null => {
  if (isEmptyChangeValue(value)) return null;

  if (change.enumType) {
    return value
      .split(/,\s*/)
      .map((item) => {
        const key = `enums.${change.enumType}.${item}`;
        if (!i18n.exists(key)) reportMissingChangeTranslation(key);
        return t(key, { defaultValue: item });
      })
      .join(', ');
  }
  if (value === 'true' || value === 'false') return t(value === 'true' ? 'common.yes' : 'common.no');
  if (DATE_TIME_FIELDS.has(change.field)) return formatLocalized(value, 'dateTime', i18n) || value;
  return value;
};

/**
 * Label of a field: the wording for this entity (`changeHistory.fields.<entity>.<field>`, for a field that means something else
 * on it), else the shared one (`changeHistory.fields.<field>`), else the raw key (reported, so the translation gets added).
 */
export const formatChangeFieldLabel = (field: string, entity: string, t: TFunction, i18n: I18n): string => {
  const keys = [`changeHistory.fields.${entity}.${field}`, `changeHistory.fields.${field}`];
  if (!i18n.exists(keys)) reportMissingChangeTranslation(keys[0]);
  return t(keys, { defaultValue: field });
};
